"use client";

import { cn } from "@ui/lib/utils";
import { forwardRef, useCallback, useEffect, useMemo, useState } from "react";

interface TextHighlighterProps {
  characters: string[];
  className?: string;
  // The characters that are typed into the editor - used to display the full text when streaming response is partially received
  displayCharacters?: string[];
  endTimes: number[];
  mediaElement: HTMLAudioElement | null;
  onClick?: (characterIndex: number) => void;
  startTimes: number[];
  withBrackets?: boolean;
}

export const TextHighlighter = forwardRef<HTMLDivElement, TextHighlighterProps>(function TextHighlighter(
  { mediaElement, characters, startTimes, endTimes, className, onClick, withBrackets = true, displayCharacters },
  ref,
) {
  const [currentTime, setCurrentTime] = useState(0);

  // Calculate words and their boundaries
  const words = useMemo(() => {
    const wordList: { endIndex: number; isBracketed?: boolean; startIndex: number; text: string }[] = [];
    let currentStart = 0;
    let currentState: "bracket" | "whitespace" | "word" | null = null;

    // Use displayCharacters if provided and characters array is empty/smaller
    const sourceChars =
      displayCharacters && (!characters.length || characters.length < displayCharacters.length)
        ? displayCharacters
        : characters;

    // Helper function to add current segment to word list
    const addCurrentSegment = (endIndex: number) => {
      if (currentState && currentStart <= endIndex) {
        const text = sourceChars.slice(currentStart, endIndex + 1).join("");
        wordList.push({
          text,
          startIndex: currentStart,
          endIndex,
          isBracketed: currentState === "bracket",
        });
      }
    };

    for (let i = 0; i < sourceChars.length; i++) {
      const char = sourceChars[i];
      const isWhitespace = /\s/.test(char);
      let newState: "bracket" | "whitespace" | "word" | null = null;

      // Determine what state we should be in
      if (char === "[") {
        newState = "bracket";
      } else if (currentState === "bracket" && char !== "]") {
        newState = "bracket"; // Continue bracket until we hit ]
      } else if (currentState === "bracket" && char === "]") {
        newState = "bracket"; // End bracket on ] (this will be the last character of the bracket)
      } else if (isWhitespace) {
        newState = "whitespace";
      } else {
        newState = "word";
      }

      // Handle state transitions
      if (newState !== currentState) {
        // End previous segment
        if (currentState) {
          addCurrentSegment(i - 1);
        }

        // Start new segment
        currentState = newState;
        currentStart = i;
      }

      // Special case: if we just processed a closing bracket, force end the bracket state
      if (currentState === "bracket" && char === "]") {
        addCurrentSegment(i);
        currentState = null;
        currentStart = i + 1;
      }
    }

    // Add the final segment
    if (currentState) {
      addCurrentSegment(sourceChars.length - 1);
    }

    return wordList;
  }, [characters, displayCharacters]);

  // Update current time when audio plays
  useEffect(() => {
    if (!mediaElement) return;

    let animationFrameId: number;

    const updateTime = () => {
      setCurrentTime(mediaElement.currentTime);
      animationFrameId = requestAnimationFrame(updateTime);
    };

    // Start the animation loop
    animationFrameId = requestAnimationFrame(updateTime);

    // Still listen for timeupdate as a fallback and for accuracy
    mediaElement.addEventListener("timeupdate", () => {
      // This ensures accuracy at key points
      setCurrentTime(mediaElement.currentTime);
    });

    return () => {
      cancelAnimationFrame(animationFrameId);
      mediaElement.removeEventListener("timeupdate", updateTime);
    };
  }, [mediaElement]);

  const handleClick = useCallback(
    (e: React.KeyboardEvent<HTMLSpanElement> | React.MouseEvent<HTMLSpanElement>, characterIndex: number) => {
      e.stopPropagation();
      onClick?.(characterIndex);
    },
    [onClick],
  );

  // Determine if a word should be highlighted based on current time
  const isWordHighlighted = (word: { endIndex: number; startIndex: number }) => {
    return currentTime >= startTimes[word.startIndex] && currentTime <= endTimes[word.endIndex];
  };

  return (
    <div
      className={cn("cursor-text whitespace-pre-wrap", className)}
      onClick={e => handleClick(e, characters.length)}
      onKeyDown={e => {
        if (e.key === "Enter" || e.key === " ") {
          handleClick(e, characters.length);
        }
      }}
      ref={ref}
      role='button'
      tabIndex={0}
    >
      {words.map((word, index) => {
        const isStreamingWord = displayCharacters && (!characters.length || word.startIndex >= characters.length);
        return (
          // eslint-disable-next-line jsx-a11y/interactive-supports-focus, jsx-a11y/click-events-have-key-events
          <span
            className={cn("effect-color effect-opacity cursor-default", {
              "bg-informative-light": isWordHighlighted(word),
              "bracketed-text whitespace-nowrap rounded-sm border border-[#B743C6] border-opacity-20 px-1 py-1 text-[#B743C6]":
                withBrackets && word.isBracketed,
              "opacity-50": isStreamingWord,
            })}
            key={`word-${index}`}
            onClick={e => handleClick(e, word.startIndex)}
            role='button'
          >
            {word.text}
          </span>
        );
      })}
    </div>
  );
});
